import { useRef, useState } from "react";
import { ImageUp, Loader2, Trash2 } from "lucide-react";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { upload, del } from "@/utils/service";
import { apiMessage } from "@/utils/api";
import { cn } from "@/lib/utils";

interface WithLogo { logo_url?: string | null }

/** Upload, replace or remove an organization logo. `endpoint` takes POST (multipart "logo") and DELETE. */
export default function LogoUploader<T extends WithLogo>({ logoUrl, name, endpoint, onChange, disabled, dark }: {
  logoUrl?: string | null;
  name: string;
  endpoint: string;
  onChange: (organization: T) => void;
  disabled?: boolean;
  dark?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (file?: File) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { notify("Choose a PNG, JPG or WebP image."); return; }
    if (file.size > 2 * 1024 * 1024) { notify("Choose an image smaller than 2 MB."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const r = await upload<{ data: { organization: T } }>(endpoint, fd);
      onChange(r.data.organization);
      notify("Logo updated", { tone: "success" });
    } catch (e) {
      notify(apiMessage(e, "Could not upload the logo."));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    if (!await confirmDialog(`Remove ${name}'s logo?`)) return;
    setBusy(true);
    try {
      const r = await del<{ data: { organization: T } }>(endpoint);
      onChange(r.data.organization);
      notify("Logo removed");
    } catch (e) {
      notify(apiMessage(e, "Could not remove the logo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      <button type="button" disabled={disabled || busy} onClick={() => fileRef.current?.click()} title={disabled ? undefined : "Upload logo"}
        className={cn("flex h-20 w-40 items-center justify-center overflow-hidden rounded-xl border-2 border-dashed p-2 transition-colors disabled:cursor-default",
          dark ? "border-gray-700 bg-gray-900 hover:border-gray-500" : "border-gray-200 bg-gray-50 hover:border-primary/50")}>
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          : logoUrl ? <img src={logoUrl} alt={`${name} logo`} className="max-h-full max-w-full object-contain" />
          : <span className={cn("flex flex-col items-center gap-1 text-[11px]", dark ? "text-gray-400" : "text-gray-500")}><ImageUp className="h-5 w-5" />Add logo</span>}
      </button>
      {!disabled && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={busy}
              className={cn("inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium disabled:opacity-60", dark ? "bg-white text-gray-900 hover:bg-gray-100" : "bg-gray-900 text-white hover:bg-gray-800")}>
              <ImageUp className="h-3.5 w-3.5" />{logoUrl ? "Replace" : "Upload"}
            </button>
            {logoUrl && (
              <button type="button" onClick={remove} disabled={busy}
                className={cn("inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium disabled:opacity-60", dark ? "border-gray-700 text-gray-200 hover:bg-gray-800" : "border-gray-200 text-gray-700 hover:bg-gray-50")}>
                <Trash2 className="h-3.5 w-3.5" />Remove
              </button>
            )}
          </div>
          <p className={cn("text-[11px]", dark ? "text-gray-500" : "text-gray-400")}>PNG, JPG or WebP, up to 2 MB. A wide logo on a transparent background works best.</p>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}
